"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowLeft, TrendingUp, ShoppingBag, Globe as Globe2, CircleCheck as CheckCircle2, Calendar, Sparkles, Target, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Card, CardContent } from "@/components/ui/card";
export default function CaseHero() {
  const shouldReduceMotion = useReducedMotion();
  const keyStats = [{
    metric: "+420%",
    label: "Organic Traffic",
    subtext: "Total non-brand search traffic increase",
    icon: TrendingUp
  }, {
    metric: "+280%",
    label: "Organic Revenue",
    subtext: "Direct revenue growth from organic search",
    icon: ShoppingBag
  }, {
    metric: "+65%",
    label: "Conversion Rate",
    subtext: "Commercial category and product conversions",
    icon: Zap
  }, {
    metric: "Top 5",
    label: "Target Rankings",
    subtext: "Top 5 positions for 25+ core fashion terms",
    icon: Target
  }];
  const highlights = ["Comprehensive Keyword Strategy & Intent Mapping", "Technical SEO Audit & Indexation Remediation", "High-Impact On-Page SEO Architecture", "Conversion Rate Optimization & Funnel Tuning"];
  return <section id="casestudyhero" data-nav="light" className="relative overflow-hidden bg-background py-12 md:py-20 lg:py-24 border-b border-border">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-32 right-1/4 h-96 w-96 rounded-lg bg-secondary/60 blur-3xl" />
        <div className="absolute top-1/2 -left-24 h-80 w-80 rounded-lg bg-primary/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/HomePage">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/CaseStudiesPage">Case Studies</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Fashion Retailer Case Study</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <Link to="/CaseStudiesPage" className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft className="size-4" />
            Back to All Case Studies
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          <div className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2.5">
              <Badge variant="secondary" className="px-3 py-1 font-medium text-foreground bg-secondary/80 border border-border">
                <ShoppingBag className="size-3.5 text-primary" />
                Ecommerce SEO Case Study
              </Badge>
              <Badge variant="outline" className="px-3 py-1 text-muted-foreground">
                <Globe2 className="size-3.5 text-primary" />
                Fashion & Apparel
              </Badge>
              <Badge variant="outline" className="px-3 py-1 text-muted-foreground">
                <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                8-Month Engagement
              </Badge>
            </div>

            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground leading-[1.15]">
                Transforming a Fashion Retailer’s Organic Performance
              </h1>
              
              <p className="text-lg sm:text-xl text-muted-foreground font-sans leading-relaxed">
                How an 8-month hands-on consultancy retainer delivering keyword strategy, on-page SEO, technical architecture, and conversion optimization unlocked +420% organic traffic for a mid-sized online fashion retailer.
              </p>
            </div>

            <div className="p-4 sm:p-5 rounded-lg bg-card border border-border shadow-xs">
              <div className="flex items-start gap-3.5">
                <div className="p-2 rounded-md bg-primary/10 text-primary shrink-0">
                  <Sparkles className="size-5" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-foreground">
                    Direct Hands-On Execution
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    I personally audited and overhauled the retailer&apos;s keyword strategy across core commercial categories, fixed faceted navigation and indexation bottlenecks, and restructured on-page technical elements to capture high-converting search intent.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                Core Transformation Focus
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {highlights.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-2.5 text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>{item}</span>
                  </div>)}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-4">
              <Button asChild size="lg" className="rounded-md font-medium shadow-sm">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md font-medium">
                <Link to="/EcommerceSeoPage">
                  Explore Ecommerce SEO Services
                </Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            ease: "easeOut"
          }}>
              <Card className="border border-border/80 shadow-md bg-card overflow-hidden">
                <div className="bg-primary px-6 py-5 text-primary-foreground">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider opacity-90">
                      Verified Results Snapshot
                    </span>
                    <Badge variant="secondary" className="bg-background/20 text-primary-foreground border-none text-xs">
                      8-Month Performance
                    </Badge>
                  </div>
                  <p className="mt-2 text-2xl sm:text-3xl font-bold font-serif">
                    +420% Organic Traffic
                  </p>
                  <p className="text-xs text-primary-foreground/80 mt-0.5">
                    Verified commercial organic search growth
                  </p>
                </div>

                <CardContent className="p-6 space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    {keyStats.map((stat, i) => {
                    const Icon = stat.icon;
                    return <div key={i} data-index={i} className="p-4 rounded-md bg-background border border-border flex flex-col justify-between">
                          <div className="flex items-center justify-between text-muted-foreground mb-2">
                            <Icon className="size-4 text-primary" />
                          </div>
                          <div>
                            <div className="text-2xl sm:text-3xl font-bold text-foreground font-serif tracking-tight">
                              {stat.metric}
                            </div>
                            <div className="text-xs font-semibold text-foreground mt-1">
                              {stat.label}
                            </div>
                            <div className="text-[12.5px] text-muted-foreground mt-0.5 leading-tight">
                              {stat.subtext}
                            </div>
                          </div>
                        </div>;
                  })}
                  </div>

                  <div className="rounded-md border border-border p-4 bg-muted/40 text-xs text-muted-foreground space-y-2.5">
                    <div className="flex items-center justify-between font-semibold text-foreground">
                      <span>Client</span>
                      <span className="font-normal text-muted-foreground">Confidential (Fashion Retailer)</span>
                    </div>
                    <div className="flex items-center justify-between font-semibold text-foreground">
                      <span>Industry</span>
                      <span className="font-normal text-muted-foreground">Fashion & Apparel</span>
                    </div>
                    <div className="flex items-center justify-between font-semibold text-foreground">
                      <span>Project Duration</span>
                      <span className="font-normal text-muted-foreground">8 Months</span>
                    </div>
                    <div className="flex items-center justify-between font-semibold text-foreground">
                      <span>Consultancy Model</span>
                      <span className="font-normal text-muted-foreground">Direct Freelance Retainer</span>
                    </div>
                    <div className="pt-1 border-t border-border/60 flex flex-col gap-1">
                      <span className="font-semibold text-foreground">Services Provided</span>
                      <span className="font-normal text-muted-foreground leading-relaxed">
                        Keyword Strategy, On-Page SEO, Technical SEO, Conversion Optimization
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}